// Used to collect phone/address info from users from several places
// (post join, post login for grandfathered accounts, post registration for when contact info was unparseable)
import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import tw from "twin.macro";
import styled from "styled-components";
import connectComponent from "../../redux/connectComponent";
import { Redirect } from "react-router-dom";
import Errors from "components/errors";
import { BoxForm, BoxHeadline, InputForm, WButton } from "components/BoxForm";
import PhoneField from "../shared/forms/phoneField";
import AddressAutocompleteField from "../shared/forms/addressAutocompleteField";
import { join, tagJoin } from "screens/project/membership";

// import ProjectHeader from "./header"
import withWrap from "components/withWrap";

// Check if email is a placeholder from phone-only OTP signup
const isPlaceholderEmail = (email) =>
  email?.includes("@placeholder.dwellsocial.com");

const SubHeadline = styled.p`
  ${tw`mb-6`}
  color: #718096;
`;

const ErrorMessage = styled.p`
  ${tw`pt-2 pb-0 mb-0`}
  color: #d10d0d;
`;

const FormGroup = styled.div`
  ${tw`mb-4`}
`;

const FormRow = styled.div`
  ${tw`flex flex-wrap -mx-1`}
`;

const FormCol = styled.div`
  ${tw`mb-4 px-1`}
  flex: 0 0 100%;
  max-width: 100%;

  ${({ md }) =>
    md &&
    `
    @media (min-width: 768px) {
      flex: 0 0 ${(md / 12) * 100}%;
      max-width: ${(md / 12) * 100}%;
    }
  `}
`;

const FormControl = styled.input`
  ${tw`block w-full`}
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

// form helpers
const Label = (labelProps) => (
  <label htmlFor={labelProps.id}>{labelProps.children}</label>
);

const Input = (inputProps) => (
  <FormControl
    id={inputProps.id}
    ref={inputProps.inputRef}
    type="text"
    defaultValue={inputProps.value}
  />
);

const Field = (fieldProps) => (
  <FormCol md={fieldProps.md}>
    <Label id={fieldProps.id}>{fieldProps.label}</Label>
    <Input
      id={fieldProps.id}
      inputRef={fieldProps.inputRef}
      type={fieldProps.type || "text"}
      {...(fieldProps.inputProps || {})}
    />
  </FormCol>
);

export class UpdateInfo extends PureComponent {
  static propTypes = {
    actions: PropTypes.object,
    authentication: PropTypes.object,
    database: PropTypes.object,
    location: PropTypes.object,
    id: PropTypes.any,
    history: PropTypes.object,
    onSuccess: PropTypes.func,
    onFailure: PropTypes.func,
    renderHeading: PropTypes.bool
  };

  // Get user from location state (passed directly from OTP flow) or fall back to Redux
  getUser = () => {
    const locationUser = this.props.location?.state?.user;
    const reduxUser = this.props.database?.authentication?.user();
    return locationUser || reduxUser;
  };

  state = {
    errors: {},
    contact: {},
    updated: false,
    redirectTo: null
  };

  constructor(props) {
    super(props);
    this.initBasicRefs();
    this.initHomeRefs();
  }

  initBasicRefs = () => {
    this.phoneRef = React.createRef();
    this.emailRef = React.createRef();
    this.nameRef = React.createRef();
  };

  initHomeRefs = () => {
    // Home fields for update
    this.streetRef = React.createRef();
    this.unitNumberRef = React.createRef();
    this.cityRef = React.createRef();
    this.stateRef = React.createRef();
    this.postalCodeRef = React.createRef();
  };

  // Determine which fields need to be collected
  needsName = (user) => !user?.name || user?.name?.trim().length < 3;
  needsEmail = (user) => !user?.email || isPlaceholderEmail(user?.email);
  needsPhone = (user) => !user?.phone;
  needsAddress = (user) => !user?.street || !user?.postal_code;

  // address = props => DB.normalizedHome(props)
  //
  // phone = props => DB.normalizedPhone(props)
  //
  handleSubmit = (event) => {
    // console.log("Handling Submit", this.addressRef.current.value)
    event.preventDefault();

    const currentUser = this.getUser();
    const user = {};

    // Only include fields that need to be updated
    if (this.needsName(currentUser)) {
      user.name = this.nameRef.current?.value;
    }

    if (this.needsPhone(currentUser)) {
      user.phone = this.phoneRef.current?.value;
    }

    if (this.needsEmail(currentUser)) {
      user.email = this.emailRef.current?.value;
    }

    if (this.needsAddress(currentUser)) {
      Object.assign(
        user,
        Object.keys(this.state.contact).length
          ? this.state.contact
          : {
              street: this.streetRef.current?.value,
              city: this.cityRef.current?.value,
              state: this.stateRef.current?.value,
              postal_code: this.postalCodeRef.current?.value
            }
      );
    }

    // Validate only the fields being submitted
    if (this.needsName(currentUser) && !this.validName(user.name)) {
      this.addError(
        "name",
        "Please enter your full name (at least 3 characters)"
      );
      return false;
    }

    if (this.needsAddress(currentUser) && !this.validAddress(user)) {
      this.addError("address", "Must provide a valid address");
      return false;
    }

    if (this.needsPhone(currentUser) && !this.validPhone(user.phone)) {
      this.addError("phone", "Must provide a valid phone");
      return false;
    }

    if (this.needsEmail(currentUser) && !this.validEmail(user.email)) {
      this.addError("email", "Must provide a valid email");
      return false;
    }

    this.props.actions
      .updateUser(user)
      .then(this.onUpdate)
      .catch(
        (err) =>
          console.log("ERR", err) ||
          (err.then && err.then((errObj) => this.addErrors(errObj.errors)))
      );

    return false;
  };

  validEmail = (email) => email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  validName = (name) => name && name.trim().length >= 3;

  addError = (key, error) => this.addErrors({ [key]: error });

  addErrors = (newErrors) => {
    let errors = { ...this.state.errors, ...newErrors };
    this.setState({ errors: errors });
  };

  projectId = (props) => props.id;

  // Get referrer from location state (e.g., checkout URL)
  getReferrer = () => this.props.location?.state?.referrer;

  // Go to the project show or referrer
  onUpdate = () =>
    this.props.actions.checkAuthentication().then(() => {
      const referrer = this.getReferrer();

      // If we have a referrer (e.g., from checkout), redirect there
      if (referrer) {
        this.setState({ updated: true, redirectTo: referrer });
        return;
      }

      // Legacy behavior: join project if projectId exists
      if (this.projectId(this.props)) {
        join(this.props, this.projectId(this.props))
          .then(() => tagJoin(this.props))
          .catch((err) => console.log("Join after Info Err", err))
          .then(() => this.setState({ updated: true }));
      } else {
        this.setState({ updated: true });
      }
    });

  // Address needs to be more than a zipcode
  validAddress = (user) =>
    user?.street?.length && user?.postal_code?.match(/^\d{5}$/);

  validPhone = (phone) => phone && phone.match(/\d/g).length === 10;

  user = (props) => props.database.authentication.user();

  // Autocomplete Address (for non-existing address)
  renderAddressField = () => (
    <InputForm>
      <label htmlFor="address">Home Address</label>

      <AddressAutocompleteField
        id="address"
        name="address"
        onChange={this.setAddress}
        required="required"
      />

      <Errors errors={this.state.errors.address} />
    </InputForm>
  );

  // Full fields for editing home
  renderAddressFields = (user) => (
    <React.Fragment>
      <FormRow>
        <Field
          id="user-home-street"
          inputRef={this.streetRef}
          label="Street"
          inputProps={{
            size: 20,
            value: user.street,
            autoFill: "street-address"
          }}
          md={8}
        />
      </FormRow>
      <FormRow>
        <Field
          id="user-home-city"
          inputRef={this.cityRef}
          label="City"
          inputProps={{ value: user.city }}
          md={6}
        />
        <Field
          id="user-home-state"
          inputRef={this.stateRef}
          label="State"
          inputProps={{ value: user.state }}
          md={3}
        />
        <Field
          id="user-home-postal_code"
          inputRef={this.postalCodeRef}
          label="Postal Code"
          inputProps={{ value: user.postal_code, autoFill: "postal-code" }}
          md={3}
        />
      </FormRow>
    </React.Fragment>
  );

  // renderHeading()
  //  <div className="container">
  //         <h6 className="green"><strong>Step 1</strong> Verify Your Information</h6>
  //         <p>We need to be sure your address is in the group&#39;s service area and we&#39;ll need a way to reach you.</p>
  //       </div>

  setAddress = (contact) => this.setState({ contact });

  shouldGoToDashboard = (props, state) =>
    !this.projectId(props) && state.updated;

  shouldGoToProject = (props, state) => this.projectId(props) && state.updated;

  // Name field for users without a name
  renderNameField = () => (
    <FormGroup>
      {this.state.errors.name && (
        <ErrorMessage>{this.state.errors.name}</ErrorMessage>
      )}
      <InputForm>
        <label htmlFor="user_info_name">Full Name</label>
        <input
          type="text"
          id="user_info_name"
          name="user_info[name]"
          ref={this.nameRef}
          autoComplete="name"
          placeholder="Your full name"
          required
        />
      </InputForm>
    </FormGroup>
  );

  // Email field for users with placeholder email (phone OTP signup)
  renderEmailField = () => (
    <FormGroup>
      {this.state.errors.email && (
        <ErrorMessage>{this.state.errors.email}</ErrorMessage>
      )}
      <InputForm>
        <label htmlFor="user_info_email">Email Address</label>
        <input
          type="email"
          id="user_info_email"
          name="user_info[email]"
          ref={this.emailRef}
          placeholder="you@example.com"
          required
        />
      </InputForm>
    </FormGroup>
  );

  render() {
    // Get user from location state (OTP flow) or Redux
    const user = this.getUser();

    // Redirect to referrer if set (e.g., back to checkout)
    if (this.state.redirectTo) {
      return <Redirect to={this.state.redirectTo} />;
    }

    if (this.shouldGoToProject(this.props, this.state)) {
      return <Redirect to={"/projects/" + this.projectId(this.props)} />;
    }

    if (this.shouldGoToDashboard(this.props, this.state)) {
      return <Redirect to="/dashboard" />;
    }

    const showName = this.needsName(user);
    const showEmail = this.needsEmail(user);
    const showPhone = this.needsPhone(user);
    const showAddress = this.needsAddress(user);

    return (
      <BoxForm onSubmit={this.handleSubmit}>
        {this.props.renderHeading ? (
          this.props.renderHeading()
        ) : (
          <React.Fragment>
            <BoxHeadline>Complete your profile</BoxHeadline>
            <SubHeadline>
              We need a few more details to complete your account.
            </SubHeadline>
          </React.Fragment>
        )}

        {this.state.error}

        <fieldset>
          {showName && this.renderNameField()}

          {showEmail && this.renderEmailField()}

          {showAddress && (
            <FormGroup>
              {user?.street?.length && user?.postal_code?.length
                ? this.renderAddressFields()
                : this.renderAddressField(this.props)}
            </FormGroup>
          )}

          {showPhone && (
            <FormGroup>
              {this.state.errors.phone && (
                <ErrorMessage>
                  {this.phoneRef.current?.value + " " + this.state.errors.phone}
                </ErrorMessage>
              )}
              <PhoneField
                id="user_info_phone"
                title="Mobile Phone"
                name="user_info[phone]"
                inputRef={this.phoneRef}
              />
            </FormGroup>
          )}
        </fieldset>

        <WButton value="Finish" type="submit" data-testid="update-info" />
      </BoxForm>
    );
  }
}

export default withWrap(
  connectComponent(UpdateInfo, ["authentication", "users"])
);
